<script setup lang="ts">
import type Menu from 'primevue/menu';
import type { MenuPassThroughOptions } from 'primevue/menu';
import type { MenuItem } from 'primevue/menuitem';
import { computed, ref } from 'vue';

import VFormTextInput from '@modules/forms/components/inputs/VFormTextInput/VFormTextInput.vue';
import { TRANSLATION_GROUP_TABLE } from '@modules/tables/constants';

import type { ITableMobileMenuProps, ITableMobileMenuSlots } from './types';

const props = defineProps<ITableMobileMenuProps>();
defineSlots<ITableMobileMenuSlots>();

const search = ref<string>();
const menu = ref<InstanceType<typeof Menu>>();

const filteredColumns = computed(() => (
  props.columns?.filter((col) => (search.value ? col.label.toLowerCase().includes(search.value.toLowerCase()) : true)) ?? []
));

const menuItems = computed<MenuItem[]>(() => filteredColumns.value?.map(({ id, name, label }) => ({ id, name, label })));

const menuPt = computed<MenuPassThroughOptions>(() => ({
  root: { class: props.minWidth ? `min-w-[${props.minWidth}]` : '' },
  list: { class: `max-h-[220px] overflow-y-auto ${props.noPaddingList ? 'p-0' : ''}` },
}));

function openMenu(ev: MouseEvent): void {
  menu.value?.toggle(ev);
}

function onSearch(value?: string): void {
  search.value = value;
}
</script>

<template>
  <div v-data-qa-auto-id="'VTableMobileMenu'">
    <slot
      name="default"
      :open-menu="openMenu"
    />
    <PMenu
      ref="menu"
      :model="menuItems"
      popup
      :pt="menuPt"
    >
      <template #start>
        <slot name="search">
          <VFormTextInput
            v-if="columns && columns.length > 5"
            :label="$getTranslation(TRANSLATION_GROUP_TABLE, 'SEARCH_PLACEHOLDER')"
            :value="search"
            icon-left="pi pi-search"
            allow-clear
            hide-errors
            @input="onSearch"
          />
        </slot>
      </template>
      <template #item="{ item }">
        <div @click.stop>
          <slot
            name="item"
            :column="item"
          />
        </div>
      </template>
      <template
        v-if="!$slots.search"
        #end
      >
        <div
          v-if="!filteredColumns?.length"
          class="flex min-h-[42px] items-center px-4 py-2 text-muted-color"
        >
          {{ $getTranslation(TRANSLATION_GROUP_TABLE, 'NO_DATA') }}
        </div>
      </template>
    </PMenu>
  </div>
</template>
